import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import { createClientKeyBundle } from '../crypto/keyBundle';
import { setClientSessionKey } from '../crypto/sessionKeyStore';

function sanitizeClientId(name, caseNumber = '') {
  const safeName = name
    .replace(/[<>:"/\\|?*]/g, '')
    .trim()
    .replace(/\s+/g, '_');

  if (!caseNumber) {
    return safeName;
  }

  const safeCase = caseNumber
    .replace(/[<>:"/\\|?*]/g, '')
    .trim()
    .replace(/\s+/g, '_');

  return `${safeName}_${safeCase}`;
}

function ClientList({ clients, setClients, onSelectClient }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newClient, setNewClient] = useState({
    client_name: '',
    case_number: '',
    passphrase: '',
    confirm_passphrase: '',
  });
  const [creatingClient, setCreatingClient] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      setLoading(true);
      const data = await apiClient.getClients();
      setClients(data.clients || []);
      setError(null);
    } catch (err) {
      setError('Failed to load clients');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();

    if (newClient.passphrase.length < 12) {
      setError('Passphrase must be at least 12 characters.');
      return;
    }
    if (newClient.passphrase !== newClient.confirm_passphrase) {
      setError('Passphrase confirmation does not match.');
      return;
    }

    try {
      setCreatingClient(true);
      const predictedClientId = sanitizeClientId(newClient.client_name, newClient.case_number);
      const { keyBundle, dekBytes } = await createClientKeyBundle({
        clientId: predictedClientId,
        passphrase: newClient.passphrase,
      });

      const result = await apiClient.createClient({
        client_name: newClient.client_name,
        case_number: newClient.case_number,
        key_bundle: keyBundle,
      });

      setClientSessionKey(result.client_id, dekBytes);
      setClients([...clients, result]);
      setNewClient({ client_name: '', case_number: '', passphrase: '', confirm_passphrase: '' });
      setShowCreateForm(false);
      setError(null);
    } catch (err) {
      setError(`Failed to create client: ${err.message}`);
      console.error(err);
    } finally {
      setCreatingClient(false);
    }
  };

  const handleSelectClient = (client) => {
    onSelectClient(client);
    navigate('/upload');
  };

  if (loading) {
    return <div className="card text-center">Loading clients...</div>;
  }

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Client Workspaces</h2>
          <button
            className="btn btn-primary"
            onClick={() => setShowCreateForm(!showCreateForm)}
          >
            {showCreateForm ? 'Cancel' : '+ New Client'}
          </button>
        </div>

        {error && (
          <div className="badge badge-error mb-4">{error}</div>
        )}

        {showCreateForm && (
          <form onSubmit={handleCreateClient} className="mb-4">
            <div className="grid grid-2">
              <div className="form-group">
                <label className="form-label">Client Name</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Smith, John"
                  value={newClient.client_name}
                  onChange={(e) => setNewClient({ ...newClient, client_name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Case Number (Optional)</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="2024-DR-001234"
                  value={newClient.case_number}
                  onChange={(e) => setNewClient({ ...newClient, case_number: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-2">
              <div className="form-group">
                <label className="form-label">Client Passphrase</label>
                <input
                  type="password"
                  className="form-input"
                  placeholder="At least 12 characters"
                  value={newClient.passphrase}
                  onChange={(e) => setNewClient({ ...newClient, passphrase: e.target.value })}
                  minLength={12}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Confirm Passphrase</label>
                <input
                  type="password"
                  className="form-input"
                  placeholder="Re-enter passphrase"
                  value={newClient.confirm_passphrase}
                  onChange={(e) => setNewClient({ ...newClient, confirm_passphrase: e.target.value })}
                  minLength={12}
                  required
                />
              </div>
            </div>
            <button type="submit" className="btn btn-primary" disabled={creatingClient}>
              {creatingClient ? 'Creating...' : 'Create Client'}
            </button>
          </form>
        )}

        {clients.length === 0 ? (
          <p className="text-muted text-center">No clients yet. Create one to get started.</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Client Name</th>
                <th>Case Number</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((client) => (
                <tr key={client.client_id}>
                  <td>
                    <strong>{client.client_name}</strong>
                    <div className="text-sm text-muted">{client.client_id}</div>
                  </td>
                  <td>{client.case_number || '-'}</td>
                  <td className="text-sm text-muted">
                    {new Date(client.created_at).toLocaleDateString()}
                  </td>
                  <td>
                    <div className="flex gap-2">
                      <button
                        className="btn btn-primary"
                        onClick={() => handleSelectClient(client)}
                      >
                        Upload Files
                      </button>
                      <Link
                        to={`/files/${client.client_id}`}
                        className="btn btn-secondary"
                      >
                        Browse Files
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default ClientList;
